import { useEffect, useMemo, useRef, useState } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { BillReviewerMultiSelect } from "@/components/BillReviewerMultiSelect";
import { CurrencyCombobox } from "@/components/CurrencyCombobox";
import { EnumMultiSelect } from "@/components/EnumMultiSelect";
import type { EnumOption } from "@/lib/enum-options";
import { cardLabel } from "@/components/ExpensesTable";
import { prettyCategory, prettyMerchant } from "@/components/ExpenseUi";
import {
  EXPENSE_ACTION_OPTIONS,
  MAX_EXPENSE_POLICY_APPROVERS,
  MAX_EXPENSE_POLICY_PRIORITY,
  PERSONAL_SPEND_OPTIONS,
  RECEIPT_CONDITION_OPTIONS,
  expenseConditionSummary,
  type ExpensePersonalSpendCondition,
  type ExpensePolicyDraft,
  type ExpenseReceiptCondition,
} from "@/lib/expense-approval-policy-utils";
import { Eyebrow } from "@/components/PageUi";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-public";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type OrganisationUserResponse = components["schemas"]["OrganisationUserResponse"];

// The merchants list endpoint caps a page at 100. Policies scope to the merchants people actually spend at,
// and the list is ordered most recently used first, so the first page covers what matters.
const MERCHANT_FETCH_LIMIT = 100;
const USER_FETCH_LIMIT = 100;

/** Fetched once per screen and shared. Best-effort: a failed lookup falls back to the raw code or a short id. */
export interface ExpensePolicyReferenceData {
  categoryOptions: EnumOption<string>[];
  merchantOptions: EnumOption<string>[];
  cardOptions: EnumOption<string>[];
  categoryLabel: (mcc: string) => string;
  merchantName: (id: string) => string;
  cardName: (id: string) => string;
  userFor: (id: string) => OrganisationUserResponse | undefined;
  /** A member's name or email; "Loading..." while it is fetched by id; "Member not found" once it is known to be gone. */
  userLabel: (id: string) => string;
}

/** Ids a screen shows that may be missing from the first page of members or merchants. */
export interface ExpensePolicyReferenceIds {
  userIds?: string[];
  merchantIds?: string[];
}

function shortId(id: string) {
  return `${id.slice(0, 8)}…`;
}

export function useExpensePolicyReferenceData(ids: ExpensePolicyReferenceIds = {}): ExpensePolicyReferenceData {
  const api = useDolfinClient();
  const [categories, setCategories] = useState<Map<string, string>>(new Map());
  const [merchants, setMerchants] = useState<Map<string, string>>(new Map());
  const [cards, setCards] = useState<Map<string, string>>(new Map());
  const [users, setUsers] = useState<Map<string, OrganisationUserResponse>>(new Map());
  const [listsLoaded, setListsLoaded] = useState(false);
  // Ids looked up one by one because the first page did not have them, and those that turned out not to exist.
  const [missingUsers, setMissingUsers] = useState<Set<string>>(new Set());
  const requested = useRef<Set<string>>(new Set());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.GET("/v1/mcc-mappings", {}),
      api.GET("/v1/merchants", { params: { query: { Limit: MERCHANT_FETCH_LIMIT } } }),
      api.GET("/v1/card-connections", { params: { query: { IncludeRemoved: true } } }),
      api.GET("/v1/users", { params: { query: { Limit: USER_FETCH_LIMIT } } }),
    ])
      .then(([mccResult, merchantResult, cardResult, userResult]) => {
        if (cancelled) return;
        setCategories(
          new Map(
            (mccResult.data ?? [])
              .map((m) => [m.mcc, prettyCategory(m.label) ?? m.mcc] as [string, string])
              .sort((a, b) => a[1].localeCompare(b[1])),
          ),
        );
        setMerchants(
          new Map(
            (merchantResult.data?.data ?? [])
              .filter((m) => m.id)
              .map((m) => [m.id!, prettyMerchant(m.displayName)] as [string, string]),
          ),
        );
        setCards(
          new Map(
            (cardResult.data ?? [])
              .filter((c) => c.id)
              .map((c) => {
                const label = cardLabel({ cardNetwork: c.network, cardLast4: c.last4 });
                return [c.id!, c.displayName ? `${c.displayName}, ${label}` : label] as [string, string];
              }),
          ),
        );
        setUsers(new Map((userResult.data?.data ?? []).filter((u) => u.id).map((u) => [u.id!, u])));
        setListsLoaded(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api]);

  // The lists are one page each, so an approver or merchant beyond it is fetched by id rather than shown as gone.
  const userKey = (ids.userIds ?? []).join(",");
  const merchantKey = (ids.merchantIds ?? []).join(",");
  useEffect(() => {
    if (!listsLoaded) return;
    // Each id is requested once, so a lookup is never cancelled by a re-render; only an unmount drops its result.
    for (const id of new Set(userKey ? userKey.split(",") : [])) {
      if (users.has(id) || requested.current.has(`u:${id}`)) continue;
      requested.current.add(`u:${id}`);
      api.GET("/v1/users/{id}", { params: { path: { id } } }).then(({ data }) => {
        if (!mounted.current) return;
        if (data) {
          setUsers((prev) => new Map(prev).set(id, { id, email: data.email, name: data.name } as OrganisationUserResponse));
        } else {
          setMissingUsers((prev) => new Set(prev).add(id));
        }
      });
    }
    for (const id of new Set(merchantKey ? merchantKey.split(",") : [])) {
      if (merchants.has(id) || requested.current.has(`m:${id}`)) continue;
      requested.current.add(`m:${id}`);
      api.GET("/v1/merchants/{id}", { params: { path: { id } } }).then(({ data }) => {
        if (mounted.current && data?.id) setMerchants((prev) => new Map(prev).set(id, prettyMerchant(data.displayName)));
      });
    }
  }, [api, listsLoaded, userKey, merchantKey, users, merchants]);

  return useMemo(
    () => ({
      // The code goes in the label too: several codes share a label, and finance teams search by code.
      categoryOptions: [...categories].map(([mcc, label]) => ({ value: mcc, label: `${label} (${mcc})` })),
      merchantOptions: [...merchants].map(([id, name]) => ({ value: id, label: name })),
      cardOptions: [...cards].map(([id, name]) => ({ value: id, label: name })),
      categoryLabel: (mcc: string) => categories.get(mcc) ?? mcc,
      merchantName: (id: string) => merchants.get(id) ?? shortId(id),
      cardName: (id: string) => cards.get(id) ?? shortId(id),
      userFor: (id: string) => users.get(id),
      userLabel: (id: string) => {
        const u = users.get(id);
        if (u) return u.name?.trim() || u.email || "Member not found";
        return missingUsers.has(id) ? "Member not found" : "Loading...";
      },
    }),
    [categories, merchants, cards, users, missingUsers],
  );
}

/** Options for a selection that may include ids missing from the fetched page, so a chip never goes blank. */
function withSelected(
  options: EnumOption<string>[],
  selected: string[],
  labelFor: (id: string) => string,
): EnumOption<string>[] {
  const known = new Set(options.map((o) => o.value));
  const missing = selected.filter((id) => !known.has(id)).map((id) => ({ value: id, label: labelFor(id) }));
  return missing.length ? [...options, ...missing] : options;
}

function ruleSentence(draft: ExpensePolicyDraft, ref: ExpensePolicyReferenceData) {
  const scope: string[] = [];
  if (draft.merchantIds.length === 1) scope.push(`at ${ref.merchantName(draft.merchantIds[0]!)}`);
  else if (draft.merchantIds.length > 1) scope.push(`at one of ${draft.merchantIds.length} merchants`);
  if (draft.cardConnectionIds.length === 1) scope.push(`on ${ref.cardName(draft.cardConnectionIds[0]!)}`);
  else if (draft.cardConnectionIds.length > 1) scope.push(`on one of ${draft.cardConnectionIds.length} cards`);
  if (draft.spenderUserIds.length === 1) scope.push(`spent by ${ref.userLabel(draft.spenderUserIds[0]!)}`);
  else if (draft.spenderUserIds.length > 1) scope.push(`spent by one of ${draft.spenderUserIds.length} people`);

  const conditions = expenseConditionSummary(
    {
      amountGte: draft.amountGte || null,
      amountLte: draft.amountLte || null,
      currency: draft.currency || null,
      mccCodes: draft.mccCodes,
      receiptCondition: draft.receiptCondition,
      personalSpend: draft.personalSpend,
    },
    ref.categoryLabel,
  );
  const approvers = draft.approverUserIds.length;
  const outcome =
    draft.action === "AutoApprove"
      ? "it posts without review"
      : draft.action === "Flag"
        ? "it is flagged as a policy violation"
        : approvers === 0
          ? "it waits for anyone who can approve expenses"
          : `it waits for ${approvers === 1 ? "the chosen approver" : `one of ${approvers} approvers`} to approve it`;
  const where = scope.length ? ` ${scope.join(" ")}` : "";
  const what = conditions === "Any expense" ? "any amount" : conditions;
  return `When an expense${where} comes in (${what}), ${outcome}.`;
}

export interface ExpenseApprovalPolicyFieldsProps {
  draft: ExpensePolicyDraft;
  onChange: (next: ExpensePolicyDraft) => void;
  referenceData: ExpensePolicyReferenceData;
  disabled?: boolean;
  /** Field id prefix, so several forms can share a page (the document review). */
  idPrefix?: string;
  /** Hide the active switch, e.g. when confirming a document (confirmed policies start active). */
  hideActive?: boolean;
  /** Hide the card and people selectors, e.g. for document proposals, which never name cards or people. */
  hideCards?: boolean;
}

export function ExpenseApprovalPolicyFields({
  draft,
  onChange,
  referenceData: ref,
  disabled = false,
  idPrefix = "expense-policy",
  hideActive = false,
  hideCards = false,
}: ExpenseApprovalPolicyFieldsProps) {
  const set = <K extends keyof ExpensePolicyDraft>(key: K, value: ExpensePolicyDraft[K]) =>
    onChange({ ...draft, [key]: value });
  const id = (name: string) => `${idPrefix}-${name}`;
  const needsApproval = draft.action === "RequireApproval";

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={id("name")}>Name</Label>
        <Input
          id={id("name")}
          placeholder="e.g. Taxis over £50"
          value={draft.name}
          maxLength={120}
          disabled={disabled}
          onChange={(e) => set("name", e.target.value)}
        />
      </div>

      <div className={cn("grid gap-4", hideActive ? "grid-cols-1" : "grid-cols-2")}>
        <div className="space-y-2">
          <Label htmlFor={id("priority")}>Priority order</Label>
          <Input
            id={id("priority")}
            type="number"
            min={0}
            max={MAX_EXPENSE_POLICY_PRIORITY}
            value={draft.priority}
            disabled={disabled}
            onChange={(e) => set("priority", e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            0 to {MAX_EXPENSE_POLICY_PRIORITY}. Lower numbers are checked first.
          </p>
        </div>
        {hideActive ? null : (
          <div className="space-y-2">
            <Label htmlFor={id("active")}>Active</Label>
            <div className="flex h-9 items-center gap-2">
              <Switch
                id={id("active")}
                checked={draft.active}
                disabled={disabled}
                onCheckedChange={(v) => set("active", v)}
              />
              <span className="text-sm text-muted-foreground">{draft.active ? "Enabled" : "Disabled"}</span>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
        <Eyebrow>Conditions</Eyebrow>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor={id("amount-gte")} className="text-xs">
              At least
            </Label>
            <Input
              id={id("amount-gte")}
              type="number"
              min={0}
              step="0.01"
              placeholder="Any"
              value={draft.amountGte}
              disabled={disabled}
              onChange={(e) => set("amountGte", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={id("amount-lte")} className="text-xs">
              At most
            </Label>
            <Input
              id={id("amount-lte")}
              type="number"
              min={0}
              step="0.01"
              placeholder="Any"
              value={draft.amountLte}
              disabled={disabled}
              onChange={(e) => set("amountLte", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={id("currency")} className="text-xs">
              Currency
            </Label>
            <CurrencyCombobox
              id={id("currency")}
              value={draft.currency}
              onChange={(v) => set("currency", v)}
              disabled={disabled}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Currency is required when an amount bound is set. The policy then only matches spend in that currency.
        </p>

        <div className="space-y-1.5">
          <Label className="text-xs">Spend categories</Label>
          <EnumMultiSelect
            options={withSelected(ref.categoryOptions, draft.mccCodes, ref.categoryLabel)}
            value={draft.mccCodes}
            onChange={(v) => set("mccCodes", v)}
            placeholder="Any category"
            searchPlaceholder="Search categories or codes..."
            disabled={disabled}
            className="w-full"
          />
        </div>

        <div className={cn("grid gap-3", hideCards ? "grid-cols-1" : "sm:grid-cols-2")}>
          <div className="space-y-1.5">
            <Label className="text-xs">Merchants</Label>
            <EnumMultiSelect
              options={withSelected(ref.merchantOptions, draft.merchantIds, ref.merchantName)}
              value={draft.merchantIds}
              onChange={(v) => set("merchantIds", v)}
              placeholder="Any merchant"
              searchPlaceholder="Search merchants..."
              disabled={disabled}
              className="w-full"
            />
          </div>
          {hideCards ? null : (
            <div className="space-y-1.5">
              <Label className="text-xs">Cards</Label>
              <EnumMultiSelect
                options={withSelected(ref.cardOptions, draft.cardConnectionIds, ref.cardName)}
                value={draft.cardConnectionIds}
                onChange={(v) => set("cardConnectionIds", v)}
                placeholder="Any card"
                searchPlaceholder="Search cards..."
                disabled={disabled}
                className="w-full"
              />
            </div>
          )}
        </div>
        {hideCards ? null : (
          <div className="space-y-1.5">
            <Label className="text-xs">People</Label>
            <BillReviewerMultiSelect
              value={draft.spenderUserIds}
              onChange={(v) => set("spenderUserIds", v)}
              placeholder="Anyone"
              disabled={disabled}
            />
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Leave empty to match everything. People match on who spent the money. A policy for particular
          merchants, cards or people takes precedence over one that applies to all.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={id("receipt")} className="text-xs">
              Receipt
            </Label>
            <Select
              value={draft.receiptCondition}
              disabled={disabled}
              onValueChange={(v) => set("receiptCondition", v as ExpenseReceiptCondition)}
            >
              <SelectTrigger id={id("receipt")} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECEIPT_CONDITION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={id("personal")} className="text-xs">
              Business or personal
            </Label>
            <Select
              value={draft.personalSpend}
              disabled={disabled}
              onValueChange={(v) => set("personalSpend", v as ExpensePersonalSpendCondition)}
            >
              <SelectTrigger id={id("personal")} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERSONAL_SPEND_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Radios, as on the bill policy form: the choice is the point of the policy. */}
      <div className="space-y-2">
        <Label id={id("action-label")}>Action</Label>
        <div role="radiogroup" aria-labelledby={id("action-label")} className="grid gap-2 sm:grid-cols-3">
          {EXPENSE_ACTION_OPTIONS.map((o) => {
            const selected = draft.action === o.value;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                onClick={() => set("action", o.value)}
                className={cn(
                  "rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-60",
                  selected && "border-accent-blue/50 bg-accent-blue/5 hover:bg-accent-blue/5",
                )}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <span
                    aria-hidden
                    className={cn("grid size-3.5 place-items-center rounded-full border", selected && "border-accent-blue")}
                  >
                    {selected ? <span className="size-1.5 rounded-full bg-accent-blue" /> : null}
                  </span>
                  {o.label}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">{o.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Approvers</Label>
        <BillReviewerMultiSelect
          value={draft.approverUserIds}
          onChange={(v) => set("approverUserIds", v)}
          disabled={disabled || !needsApproval}
        />
        <p className="text-xs text-muted-foreground">
          {needsApproval
            ? `Who may approve a matching expense. Max ${MAX_EXPENSE_POLICY_APPROVERS}. Leave empty so anyone who can approve expenses can. Admins can always approve.`
            : "Approvers only apply when the action is Require approval."}
        </p>
      </div>

      <div className="rounded-lg border border-dashed px-3 py-2.5 text-sm">
        <Eyebrow className="mb-1">In words</Eyebrow>
        {ruleSentence(draft, ref)}
      </div>
    </div>
  );
}
